import { TypographyVariant as MuiTypographyVariant } from '@mui/material';

//==============================================================================
// Utility Types
//==============================================================================
export type ThemeColorKey =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'tertiary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'default';

export type TypographyVariant = MuiTypographyVariant | 'xsButton' | 'smButton' | 'lgButton';

export interface AlignmentOptions {
  align?: 'top' | 'bottom';
  alignOffset?: number;
}

export interface PillPaletteColor {
  main: string;
  contrastText: string;
  light?: string;
  dark?: string;
}

export type RecursivePartial<T> = {
  [P in keyof T]?: Required<T>[P] extends object ? RecursivePartial<T[P]> : T[P];
};

//==============================================================================
// Settings
//==============================================================================

interface AssignedFont {
  name?: string;
  id?: number;
  url?: string;
}
export interface FontSettings {
  fontFamilyLink?: string;
  fontWeight?: string | number;
  /**
   * Font offset y for fonts that are not vertically aligned correctly by default
   */
  fontOffsetY?: number;
  /**
   * Font offset y for numeric values that are not vertically aligned correctly by default.
   * This is used in cases where only numeric values are displayed, such as the shopping bag item quantity.
   */
  numericFontOffsetY?: number;
  assignedFonts?: Partial<Record<string, AssignedFont>>;
}
export type SmartBannerType = 'ios' | 'android' | 'both' | 'none';
export interface AppSettings {
  smartBannerType?: SmartBannerType;
  iosCustomBannerAlignment?: AlignmentOptions;
  androidCustomBannerAlignment?: AlignmentOptions;
  dtcGetAppImage?: string;
}

export interface DialogSettings {
  membership?: {
    enabled?: boolean;
    dismissalPeriodDays?: number;
  };
}

export interface FeatureFlags {
  bagAnimationDot?: boolean;
  scrollSliderDisplay?: boolean;
  storeLocatorMap?: boolean;
  cartCountNumber?: boolean;
  bagAnimationDotMobile?: boolean;
  scrollSliderDisplayMobile?: boolean;
  storeLocatorMapMobile?: boolean;
  cartCountNumberMobile?: boolean;
  /** number of pixels the in-page button must scroll past before showing sticky bar (desktop) */
  scrollSliderDisplayDistance?: number;
  /** number of pixels the in-page button must scroll past before showing sticky bar (mobile) */
  scrollSliderDisplayDistanceMobile?: number;
  /** when true, non-eligible product clicks open the BuyBox dialog instead of navigating to the retailer page */
  showBuyBoxOnNonEligibleClick?: boolean;
}

export interface CustomThemeSettings {
  app?: AppSettings;
  dialog?: DialogSettings;
  featureFlags?: FeatureFlags;
  fonts?: FontSettings;
}

export type CustomThemeSettingsOptions = RecursivePartial<CustomThemeSettings>;

//==============================================================================
// Buttons
//==============================================================================
export type ButtonVariant = 'solid' | 'icon' | 'outline' | 'text';
export type ButtonHeight =
  | string
  | number
  | {
      xs?: string | number;
      sm?: string | number;
      md?: string | number;
      lg?: string | number;
      xl?: string | number;
    };

export interface BaseButtonProps {
  color?: ThemeColorKey;
  height?: ButtonHeight;
}

export interface SolidButtonPreset {
  /** the color of the button */
  color: ThemeColorKey;
  /** the color of the button text */
  contrastText: string;
  /** button radius (px) override */
  radius: number;
  fontSize?: number;
  /** the font weight of the button */
  fontWeight?: number;
}
export interface IconButtonPreset {
  /** the color of the button */
  color: ThemeColorKey;
  /** the square dimensions size (px) of the button */
  size: number;
  fontSize?: number;
  fontWeight?: number;
}
export interface OutlineButtonPreset {
  /** the color of the button */
  color: ThemeColorKey;
  /** the color of the button border. Omitted values should default to the button color */
  borderColor?: ThemeColorKey;
  /** the width of the button border (px) */
  borderWidth: number;
  /** button radius (px) override */
  radius: number;
  fontSize?: number;
  fontWeight?: number;
}
export interface TextButtonPreset {
  /** the color of the button */
  color: ThemeColorKey;
  fontSize?: number;
  fontWeight?: number;
}

export type AllButtonPresets = Partial<
  SolidButtonPreset & IconButtonPreset & OutlineButtonPreset & TextButtonPreset
> & {
  color: ThemeColorKey;
};

//==============================================================================
// Content Presets
//==============================================================================
export interface ButtonContent {
  text?: string;
  nonEligibleText?: string;
  customIconStartSrc?: string;
  customIconEndSrc?: string;
  /** Optional caption rendered beneath the icon when in icon-button mode (e.g. "Account"). */
  iconLabel?: string;
}

interface AdaptiveContentVariant<T> {
  signedOutDesktop?: T;
  signedInDesktop?: T;
  signedOutMobile?: T;
  signedInMobile?: T;
}

export type AdaptiveContentVariantKey = keyof AdaptiveContentVariant<never>;

export interface BuyBoxDialogContent {
  /** Overrides the "Ways to buy" header title */
  title?: string;
  /** Overrides the "View" button text for non-eligible products */
  viewButtonText?: string;
}

export interface ContentPresets {
  connectRewardHeader?: AdaptiveContentVariant<string>;
  membershipButton?: AdaptiveContentVariant<ButtonContent>;
  cartButton?: AdaptiveContentVariant<ButtonContent>;
  profileButton?: AdaptiveContentVariant<ButtonContent>;
  addToBagButton?: AdaptiveContentVariant<ButtonContent>;
  plpAddToBagButton?: AdaptiveContentVariant<ButtonContent>;
  buyBoxDialog?: BuyBoxDialogContent;
}

//==============================================================================
// Global Component Presets
//==============================================================================

export interface GlobalPillComponentPresets {
  default: PillPaletteColor;
  outOfStock: PillPaletteColor;
  purchase: PillPaletteColor;
  survey: PillPaletteColor;
  content: PillPaletteColor;
  affiliate: PillPaletteColor;
  dailyDeal: PillPaletteColor;
}

export interface GlobalCheckoutDrawerComponentPresets {
  customHeaderName?: string;
  customCheckoutText?: string;
  customViewBagText?: string;
  customEmptyBagMessageCardHeaderText?: string;
  /** if true, move checkout/view-bag buttons to the top on mobile */
  checkoutDrawerHeaderButton?: boolean;
}

export type GlobalPillComponentPresetsOptions = RecursivePartial<GlobalPillComponentPresets>;

export interface GlobalComponentPresets {
  pill: GlobalPillComponentPresets;
  checkoutDrawer: GlobalCheckoutDrawerComponentPresets;
}

export type GlobalComponentPresetsOptions = RecursivePartial<GlobalComponentPresets>;

export const THEME_PREFIX = 'dtc';
export const THEME_CSS_VARIABLE_PREFIX = `--${THEME_PREFIX}-`;
